import type { Color } from '@color';
import { ColorMode, ColorName, getColor } from '@color';
import { Rect, Size, type SizeConstraints } from '@geom';
import { ObjectFit } from '@image';
import { LayoutConstants } from '@layout';
import type { Visual, VisualGroupOpts } from '@visual';
import type { BorderElementConfig } from './BorderElementConfig';
import type { LayoutElement } from './LayoutElement';
import { SnorfLayout } from './SnorfLayout';
import { LayoutDirection, type CornerRadius } from './types';

/**
 * Render a `SnorfLayout` into the provided `Visual`.
 *
 * **Rendering Order (Z-Order):**
 * 1. Background fill (if LayoutElement.backgroundColor)
 * 2. Text content (if LayoutElement.text with measured paragraphId)
 * 3. Child elements (recursive rendering, sorted by zIndex ascending)
 * 4. Between-children borders
 * 5. Element borders LAST (ensuring they appear on top of child content)
 *
 * This rendering order ensures that borders are never painted over by child
 * content, which was previously an issue in complex layouts like entity cards
 * where colored header bars would overwrite container borders.
 *
 * Corner-radius: honoured only when all 4 radii are equal.
 * Per-side & between-children borders, text, images, etc. will be added later.
 *
 * @param layout    SnorfLayout, the result of `performLayout()`
 * @param visual Visual implementation (CanvasKit or SVG).
 * @param opts   includeRoot – if true, also draws the implicit `_root` box
 */
export function renderLayout(
  layout: SnorfLayout,
  visual: Visual,
  opts: { includeRoot?: boolean; mark?: string } = {}
): void {
  const mark =
    opts.mark !== undefined ? `Snorf:renderLayout:${opts.mark}` : 'Snorf:renderLayout';
  performance.mark(`${mark}:start`);
  const includeRootOpt = opts.includeRoot ?? false;
  const root = layout.getLayoutElement(SnorfLayout.ROOT_ID);

  if (includeRootOpt) {
    // If includeRoot is true, draw the entire tree starting from the implicit root.
    drawElement(layout, visual, root);
  } else {
    // If includeRoot is false (default), iterate over the children of the implicit root
    // (which are the user's top-level elements) and draw each of them.
    // This ensures user's elements are drawn without the implicit root's own visual.
    function drawChildren() {
      const children = layout.getChildren(root.id);
      for (const child of children) {
        drawElement(layout, visual, child);
      }
    }
    if (root.opacity < 1) {
      visual.withGroup({ id: root.id, opacity: root.opacity }, () => {
        drawChildren();
      });
    } else {
      drawChildren();
    }
  }
  performance.mark(`${mark}:end`);
  performance.measure(mark, `${mark}:start`, `${mark}:end`);
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

/**
 * Calculate the adjusted stroke width for an element, accounting for hover state.
 * When hovered, uses the hovered stroke width constant; otherwise uses the base width.
 */
function adjustedStrokeWidth(baseStrokeWidth: number, isHovered: boolean): number {
  return isHovered ? LayoutConstants.HOVERED_NODE_STROKE_WIDTH : baseStrokeWidth;
}

function drawElement(layout: SnorfLayout, visual: Visual, element: LayoutElement) {
  const bounds = element.bounds;

  // Resolve background colour (if any):
  const fill = element.backgroundColor?.realize(visual.colorMode);

  const uniform = borderStroke(element.border, visual);

  if (element.selectionHaloOpacity > 0) {
    // Calculate stroke width including hover adjustment for proper halo expansion
    const baseStrokeWidth = uniform?.width ?? 0;
    const strokeW = adjustedStrokeWidth(baseStrokeWidth, element.hovered);
    const opacity = element.selectionHaloOpacity * element.opacity;
    drawRectHalo(
      visual,
      bounds,
      opacity,
      element.cornerRadius,
      strokeW,
      visual.colorMode
    );
  }

  const groupOptions: Partial<VisualGroupOpts> = { id: element.id };
  if (element.opacity !== 1.0) {
    groupOptions.opacity = element.opacity;
  }

  visual.withGroup(groupOptions, _paint => {
    // Draw background first
    if (fill) {
      visual.drawRect(bounds, { cornerRadius: element.cornerRadius, fill });
    }

    // Check for text element and render text if present
    if (element.textData?.measured?.paragraphId) {
      const textData = element.textData;
      const measured = textData.measured;
      if (measured) {
        const paragraphId = measured.paragraphId;
        const paragraph = visual.cache.paragraphCache.get(paragraphId, visual.colorMode);

        if (paragraph) {
          // WORKAROUND: Call layout() again after retrieving from cache
          // to fix RTL text misalignment issues. This matches the workaround
          // used in drawDoc.ts for the same issue.
          paragraph.layout(measured.size.width);

          // Use the element's origin as the text position within its bounds
          visual.drawParagraph(paragraph, bounds, element.origin, 1.0);
        } else {
          console.warn(
            `Paragraph not found in cache for element ${element.id}, paragraphId: ${paragraphId}`
          );
        }
      }
    }

    // Check for image element and render image if present
    if (element.imageData?.source) {
      const imageData = element.imageData;
      const source = imageData.source;

      try {
        // Only proceed if the image source is loaded
        if (source.isLoaded) {
          // Calculate optimal render size based on ObjectFit mode
          // This ensures SVG images are rendered from vectors at appropriate resolution
          let renderSizeConstraints;

          switch (imageData.objectFit) {
            case ObjectFit.FILL: {
              // FILL: Render at exact container dimensions (will be stretched)
              renderSizeConstraints = new Size(
                element.dimensions.width,
                element.dimensions.height
              );
              break;
            }

            case ObjectFit.CONTAIN: {
              // CONTAIN: Calculate the exact size that would fit within container preserving aspect ratio
              const containScale = Math.min(
                element.dimensions.width / source.intrinsicSize.width,
                element.dimensions.height / source.intrinsicSize.height
              );
              renderSizeConstraints = new Size(
                Math.round(source.intrinsicSize.width * containScale),
                Math.round(source.intrinsicSize.height * containScale)
              );
              break;
            }

            case ObjectFit.COVER: {
              // COVER: Calculate the exact size that would cover container preserving aspect ratio
              const coverScale = Math.max(
                element.dimensions.width / source.intrinsicSize.width,
                element.dimensions.height / source.intrinsicSize.height
              );
              renderSizeConstraints = new Size(
                Math.round(source.intrinsicSize.width * coverScale),
                Math.round(source.intrinsicSize.height * coverScale)
              );
              break;
            }

            case ObjectFit.SCALE_DOWN: {
              // SCALE_DOWN: Like contain but never larger than intrinsic size
              const scaleDownScale = Math.min(
                1.0, // Never scale up
                element.dimensions.width / source.intrinsicSize.width,
                element.dimensions.height / source.intrinsicSize.height
              );
              renderSizeConstraints = new Size(
                Math.round(source.intrinsicSize.width * scaleDownScale),
                Math.round(source.intrinsicSize.height * scaleDownScale)
              );
              break;
            }

            case ObjectFit.NONE:
            default:
              // NONE: Use intrinsic size (no constraints)
              renderSizeConstraints = undefined;
              break;
          }

          // Get the image from cache at the optimal render size - cache will render on-demand if needed
          // Account for the visual's current transformation matrix to ensure crisp rendering at scaled resolutions
          let scaledRenderSizeConstraints: Size | SizeConstraints | undefined =
            renderSizeConstraints;
          if (renderSizeConstraints) {
            // Get the current transformation matrix to determine the scale factor
            const bounds = visual.userSpaceBounds();
            const scaleFactor = bounds.scale;

            // Apply the scale factor to the render size constraints for crisp rendering
            if (renderSizeConstraints instanceof Size) {
              scaledRenderSizeConstraints = new Size(
                Math.round(renderSizeConstraints.width * scaleFactor),
                Math.round(renderSizeConstraints.height * scaleFactor)
              );
            } else {
              // For SizeConstraints, scale the dimensions if specified
              // Type assertion is safe here since we know it's not a Size instance
              const constraints = renderSizeConstraints as SizeConstraints;
              scaledRenderSizeConstraints = {
                maxWidth: constraints.maxWidth
                  ? typeof constraints.maxWidth === 'number'
                    ? Math.round(constraints.maxWidth * scaleFactor)
                    : constraints.maxWidth // Keep percentage strings as-is
                  : undefined,
                maxHeight: constraints.maxHeight
                  ? typeof constraints.maxHeight === 'number'
                    ? Math.round(constraints.maxHeight * scaleFactor)
                    : constraints.maxHeight // Keep percentage strings as-is
                  : undefined,
                minWidth: constraints.minWidth
                  ? typeof constraints.minWidth === 'number'
                    ? Math.round(constraints.minWidth * scaleFactor)
                    : constraints.minWidth // Keep percentage strings as-is
                  : undefined,
                minHeight: constraints.minHeight
                  ? typeof constraints.minHeight === 'number'
                    ? Math.round(constraints.minHeight * scaleFactor)
                    : constraints.minHeight // Keep percentage strings as-is
                  : undefined
              } satisfies SizeConstraints;
            }
          }

          const image = visual.cache.imageCache.get(
            source.id,
            scaledRenderSizeConstraints
          );

          if (image) {
            visual.drawImage(image, bounds, {
              objectFit: imageData.objectFit
              // TODO: Add objectPosition support when needed
            });
          } else {
            console.warn(
              `Failed to get image from cache for element ${element.id}, sourceId: ${source.id}`
            );
          }
        } else {
          // Image exists but not loaded - render placeholder or skip silently
          console.warn(
            `Image source not loaded for element ${element.id}, sourceId: ${source.id}`
          );
        }
      } catch (error) {
        console.warn(`Failed to render image for element ${element.id}:`, error);
      }
    }

    // Recurse so children are nested inside this group
    // Apply clipping if requested to constrain child rendering to element bounds
    const renderChildren = () => {
      const children = layout.getChildren(element.id);

      // Sort children by zIndex (ascending order) so higher zIndex values render on top
      const sortedChildren = [...children].sort((a, b) => a.zIndex - b.zIndex);

      for (const child of sortedChildren) {
        drawElement(layout, visual, child);
      }

      // Borders between children
      drawBetweenChildrenBorders(layout, visual, element);
    };

    if (element.clip) {
      visual.withClip(bounds, renderChildren, element.cornerRadius);
    } else {
      renderChildren();
    }

    // Draw borders LAST so they appear on top of child content
    if (uniform) {
      // Adjust stroke width for hover state
      const strokeWidth = adjustedStrokeWidth(uniform.width, element.hovered);
      const adjustedStroke = { ...uniform, width: strokeWidth };

      // Uniform border stroke only (background already drawn above)
      visual.drawRect(bounds, {
        cornerRadius: element.cornerRadius,
        stroke: adjustedStroke
      });
    } else {
      // Per-side borders - use uniform corner radius for these
      const uniformRadius = uniformCornerRadius(element.cornerRadius);
      drawBorders(visual, bounds, element.border, uniformRadius);
    }

    // Draw copy overlay AFTER border using actual stroke width for proper coverage
    if (element.copyOverlayOpacity > 0) {
      const baseStrokeWidth = uniform?.width ?? 0;
      const actualStrokeWidth = adjustedStrokeWidth(baseStrokeWidth, element.hovered);
      const expand = actualStrokeWidth / 2;
      const highlightRect = bounds.expandingBy(expand);
      const baseCornerRadius = uniformCornerRadius(element.cornerRadius) ?? 0;
      visual.drawRect(highlightRect, {
        cornerRadius: baseCornerRadius + expand,
        fill: getColor(ColorName.copyHighlight, visual.colorMode).withOpacity(
          element.copyOverlayOpacity
        )
      });
    }
  });
}

function uniformCornerRadius(cr: CornerRadius): number | undefined {
  const { topLeft, topRight, bottomLeft, bottomRight } = cr;
  if (topLeft === topRight && topLeft === bottomLeft && topLeft === bottomRight) {
    return topLeft;
  }
  return undefined; // non-uniform not yet supported
}

function borderStroke(
  border: BorderElementConfig | undefined,
  visual: Visual
):
  | {
      color: Color;
      width: number;
    }
  | undefined {
  if (border === undefined) return undefined;
  const color = border.color?.realize(visual.colorMode);
  if (!color) return undefined;

  // uniform “all-sides” only:
  if (
    typeof border.width === 'object' &&
    'left' in border.width &&
    'right' in border.width &&
    'top' in border.width &&
    'bottom' in border.width
  ) {
    const w = border.width as {
      left: number;
      right: number;
      top: number;
      bottom: number;
    }; // Type assertion after check
    if (w.left === w.right && w.left === w.top && w.left === w.bottom) {
      if (w.left > 0) {
        return { color, width: w.left };
      }
    }
  }
  return undefined;
}

// Draw borders around the rectangle for per-side widths.
function drawBorders(
  visual: Visual,
  bounds: Rect,
  border: BorderElementConfig | undefined,
  cornerRadius: number | undefined
): void {
  if (border === undefined) return;

  const color = border.color?.realize(visual.colorMode);
  if (!color) return;

  const width = border.width;
  if ('betweenChildren' in width) {
    // betweenChildren handled separately
    return;
  }

  const w = width as {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
  const isUniform = w.left === w.right && w.left === w.top && w.left === w.bottom;
  if (isUniform && w.left > 0) {
    // For uniform borders, draw as stroke only (background already drawn)
    const stroke = { color, width: w.left };
    visual.drawRect(bounds, { cornerRadius, stroke });
    return;
  }

  // Draw per-side borders as filled rectangles
  if (w.left > 0) {
    visual.drawRect(
      new Rect({
        x: bounds.left,
        y: bounds.top,
        width: w.left,
        height: bounds.height
      }),
      { fill: color }
    );
  }
  if (w.right > 0) {
    visual.drawRect(
      new Rect({
        x: bounds.right - w.right,
        y: bounds.top,
        width: w.right,
        height: bounds.height
      }),
      { fill: color }
    );
  }
  if (w.top > 0) {
    visual.drawRect(
      new Rect({
        x: bounds.left,
        y: bounds.top,
        width: bounds.width,
        height: w.top
      }),
      { fill: color }
    );
  }
  if (w.bottom > 0) {
    visual.drawRect(
      new Rect({
        x: bounds.left,
        y: bounds.bottom - w.bottom,
        width: bounds.width,
        height: w.bottom
      }),
      { fill: color }
    );
  }
}

// Draw borders between children according to layout direction
function drawBetweenChildrenBorders(
  layout: SnorfLayout,
  visual: Visual,
  element: LayoutElement
): void {
  const border = element.border;
  if (border === undefined) return;
  const width = border.width;
  if (!('betweenChildren' in width)) return;
  const bw = width.betweenChildren;
  if (bw <= 0) return;

  const color = border.color?.realize(visual.colorMode);
  if (!color) return;

  const lyt = layout.getLayoutElement(element.id);
  const direction = lyt.layoutConfig.direction;
  const gap = lyt.layoutConfig.childGap;
  const halfGap = gap / 2;
  const children = layout.getChildren(element.id);

  const offset = bw / 2;

  if (direction === LayoutDirection.LEFT_TO_RIGHT) {
    for (let i = 1; i < children.length; i++) {
      const child = children[i];
      const x = child.origin.x - halfGap - offset;
      visual.drawRect(
        new Rect({
          x,
          y: element.origin.y,
          width: bw,
          height: element.dimensions.height
        }),
        { fill: color }
      );
    }
  } else if (direction === LayoutDirection.RIGHT_TO_LEFT) {
    for (let i = 1; i < children.length; i++) {
      const prev = children[i - 1];
      const x = prev.origin.x - halfGap - offset;
      visual.drawRect(
        new Rect({
          x,
          y: element.origin.y,
          width: bw,
          height: element.dimensions.height
        }),
        { fill: color }
      );
    }
  } else {
    // TOP_TO_BOTTOM
    for (let i = 1; i < children.length; i++) {
      const child = children[i];
      const y = child.origin.y - halfGap - offset;
      visual.drawRect(
        new Rect({
          x: element.origin.x,
          y,
          width: element.dimensions.width,
          height: bw
        }),
        { fill: color }
      );
    }
  }
}

function drawRectHalo(
  visual: Visual,
  bounds: Rect,
  opacity: number,
  cornerRadius: CornerRadius,
  rectStrokeWidth: number,
  mode: ColorMode
): void {
  if (opacity <= 0) return;
  const expand = (rectStrokeWidth + LayoutConstants.SELECTION_HALO_WIDTH) / 2;
  visual.drawRect(bounds.expandingBy(expand), {
    cornerRadius: (uniformCornerRadius(cornerRadius) ?? 0) + expand,
    stroke: {
      color: getColor(ColorName.selectionHalo, mode).withOpacity(opacity),
      width: LayoutConstants.SELECTION_HALO_WIDTH
    }
  });
}
